export const QIUI_LITTLE_DEVIL = Object.freeze({
  service: "8653000a-43e6-47b7-9cb0-5fc21d4ae340",
  notify: "8653000b-43e6-47b7-9cb0-5fc21d4ae340",
  write: "8653000c-43e6-47b7-9cb0-5fc21d4ae340"
});

interface BluetoothRemoteGATTCharacteristicLike
  extends EventTarget {
  startNotifications(): Promise<BluetoothRemoteGATTCharacteristicLike>;
  value?: DataView;
}

interface BluetoothRemoteGATTServiceLike {
  getCharacteristic(
    characteristic: string
  ): Promise<BluetoothRemoteGATTCharacteristicLike>;
}

interface BluetoothRemoteGATTServerLike {
  connected: boolean;
  connect(): Promise<BluetoothRemoteGATTServerLike>;
  disconnect(): void;
  getPrimaryService(service: string): Promise<BluetoothRemoteGATTServiceLike>;
}

interface BluetoothDeviceLike extends EventTarget {
  name?: string;
  gatt?: BluetoothRemoteGATTServerLike;
}

interface BluetoothNavigatorLike {
  requestDevice(options: {
    filters: Array<{ services: string[] }>;
    optionalServices: string[];
  }): Promise<BluetoothDeviceLike>;
}

export class QiuiDiagnosticBridge {
  device: BluetoothDeviceLike | null = null;
  server: BluetoothRemoteGATTServerLike | null = null;
  notifications: string[] = [];
  onDisconnect?: () => void;
  onNotification?: (frame: string) => void;

  static isSupported() {
    return "bluetooth" in navigator;
  }

  async connect() {
    if (!QiuiDiagnosticBridge.isSupported()) {
      throw new Error("web_bluetooth_not_supported");
    }
    const bluetooth = (navigator as Navigator & {
      bluetooth: BluetoothNavigatorLike;
    }).bluetooth;

    this.device = await bluetooth.requestDevice({
      filters: [{ services: [QIUI_LITTLE_DEVIL.service] }],
      optionalServices: [QIUI_LITTLE_DEVIL.service]
    });
    if (!this.device.gatt) throw new Error("bluetooth_gatt_unavailable");

    this.server = await this.device.gatt.connect();
    const service = await this.server.getPrimaryService(QIUI_LITTLE_DEVIL.service);
    const notify = await service.getCharacteristic(QIUI_LITTLE_DEVIL.notify);
    await service.getCharacteristic(QIUI_LITTLE_DEVIL.write);
    await notify.startNotifications();
    notify.addEventListener("characteristicvaluechanged", () => {
      const bytes = notify.value
        ? new Uint8Array(
            notify.value.buffer,
            notify.value.byteOffset,
            notify.value.byteLength
          )
        : new Uint8Array();
      const frame = Array.from(bytes, (byte) =>
        byte.toString(16).padStart(2, "0")
      ).join("");
      this.notifications.push(frame);
      this.notifications = this.notifications.slice(-50);
      this.onNotification?.(frame);
    });
    this.device.addEventListener("gattserverdisconnected", () => {
      this.onDisconnect?.();
    });
    return {
      name: this.device.name || "Little Devil",
      service: QIUI_LITTLE_DEVIL.service,
      notify: QIUI_LITTLE_DEVIL.notify,
      write: QIUI_LITTLE_DEVIL.write
    };
  }

  disconnect() {
    if (this.server?.connected) this.server.disconnect();
    this.device = null;
    this.server = null;
  }

  async applyOutput() {
    throw new Error("protocol_verification_required");
  }
}
